/**
 * ADMIN MODULE — Email Template.
 *
 * Ported from the source app admin "Email Setup" section, adapted to one
 * platform-level template (no per-type tabs), Supabase-Auth/platform_admins RLS,
 * and the Brand Command theme tokens. Edits the header + footer "chrome" that
 * wraps every transactional email Brand Command sends to customers, with a live
 * approximate preview. The send-email function reads these settings server-side.
 *
 * Logos are referenced by URL (paste a hosted image) rather than uploaded to a
 * bucket — a deliberate simplification of the the source app upload flow; can grow into an
 * upload later without changing the stored shape.
 */

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "../components/AdminLayout.jsx";
import { loadEmailTemplate, saveEmailTemplate, TEMPLATE_DEFAULTS } from "../utils/emailService.js";
import { loadEmailSettings, saveEmailSettings, sendTestEmail, SETTINGS_DEFAULTS } from "../utils/emailSettingsService.js";

const PLACEMENTS = ["left", "center", "right"];

export default function EmailTemplateView() {
  const [form, setForm] = useState(TEMPLATE_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const msgTimer = useRef(null);

  const showMsg = (type, text) => {
    setMsg({ type, text });
    clearTimeout(msgTimer.current);
    msgTimer.current = setTimeout(() => setMsg(null), 3500);
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await loadEmailTemplate();
        if (alive) setForm(data);
      } catch (e) {
        if (alive) showMsg("err", e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; clearTimeout(msgTimer.current); };
  }, []);

  const set = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  async function handleSave() {
    setSaving(true);
    try {
      await saveEmailTemplate({
        header_bg_color: form.header_bg_color,
        header_text: form.header_text,
        header_logo_url: form.header_logo_url,
        header_logo_placement: form.header_logo_placement,
        footer_bg_color: form.footer_bg_color,
        footer_text: form.footer_text,
        footer_logo_url: form.footer_logo_url,
        footer_logo_placement: form.footer_logo_placement,
      });
      showMsg("ok", "Email template saved.");
    } catch (e) {
      showMsg("err", e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminLayout
      title="Email"
      subtitle="How Brand Command emails customers — Brevo sender setup and branding"
    >
      <div className="admin-et">
        {/* Straight under the title: this screen configures the SENDER and the
            chrome, but says nothing about WHAT we send. The reference does, and
            without a pointer here nobody finds it — the admin nav is the other
            obvious home, but a link where the question actually arises beats a
            twelfth menu entry. */}
        <Link to="/admin/email-reference" className="admin-et-reflink">
          <span className="admin-et-reflink-title">📋 Email Reference</span>
          <span className="admin-et-reflink-sub">
            Every email the platform sends — trigger, recipients, branding and
            recent delivery. Read live.
          </span>
        </Link>

        <SendingSetup />

        <section className="admin-et-section">
          <h2 className="admin-et-h2">Branding template</h2>
          <p className="admin-et-desc">
            The chrome around every notification email — ticket replies, alerts,
            and the like. Each email's content sits between the header and footer
            below. Changes take effect on the next email sent.
          </p>

          {msg && <div className={`admin-et-msg ${msg.type}`}>{msg.text}</div>}

          {loading ? (
            <div className="admin-empty">Loading…</div>
          ) : (
            <>
              <EtPanel
                title="Header"
                bgColor={form.header_bg_color}
                logoUrl={form.header_logo_url}
                logoPlacement={form.header_logo_placement}
                text={form.header_text}
                textPlaceholder="e.g. Brand Command"
                onBgColor={(v) => set("header_bg_color", v)}
                onLogoUrl={(v) => set("header_logo_url", v)}
                onLogoPlacement={(v) => set("header_logo_placement", v)}
                onText={(v) => set("header_text", v)}
              />

              <EtPanel
                title="Footer"
                bgColor={form.footer_bg_color}
                logoUrl={form.footer_logo_url}
                logoPlacement={form.footer_logo_placement}
                text={form.footer_text}
                textPlaceholder="e.g. Questions? Reach us at info@brandcommand.app"
                onBgColor={(v) => set("footer_bg_color", v)}
                onLogoUrl={(v) => set("footer_logo_url", v)}
                onLogoPlacement={(v) => set("footer_logo_placement", v)}
                onText={(v) => set("footer_text", v)}
              />

              <EtPreview form={form} />

              <div className="admin-et-foot">
                <button className="btn" onClick={handleSave} disabled={saving}>
                  {saving ? "Saving…" : "Save Template"}
                </button>
              </div>
            </>
          )}
        </section>
      </div>
    </AdminLayout>
  );
}

function SendingSetup() {
  const [cfg, setCfg] = useState(SETTINGS_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [msg, setMsg] = useState(null);
  const timer = useRef(null);

  const showMsg = (type, text) => {
    setMsg({ type, text });
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setMsg(null), 4000);
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await loadEmailSettings();
        if (alive) { setCfg(data); setTestTo(data.from_email || ""); }
      } catch (e) {
        if (alive) showMsg("err", e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; clearTimeout(timer.current); };
  }, []);

  const set = (k, v) => setCfg((p) => ({ ...p, [k]: v }));

  async function save(patch, okText) {
    setSaving(true);
    try {
      await saveEmailSettings(patch);
      showMsg("ok", okText || "Saved.");
    } catch (e) {
      showMsg("err", e.message);
      throw e;
    } finally {
      setSaving(false);
    }
  }

  async function toggleEnabled(next) {
    set("enabled", next);
    try {
      await saveEmailSettings({ enabled: next });
    } catch (e) {
      set("enabled", !next); // revert on failure
      showMsg("err", e.message);
    }
  }

  async function handleTest() {
    setTesting(true);
    try {
      await sendTestEmail(testTo.trim());
      showMsg("ok", `Test email sent to ${testTo.trim()}. Check the inbox (and spam).`);
    } catch (e) {
      showMsg("err", e.message);
    } finally {
      setTesting(false);
    }
  }

  return (
    <section className="admin-et-section">
      <h2 className="admin-et-h2">Sending (Brevo)</h2>
      <p className="admin-et-desc">
        Two independent senders: <strong>Notification emails</strong> (ticket
        replies, alerts) and the <strong>Nurture sequence</strong> (the
        build-the-brand guide emails) can each use their own address. The Brevo
        API key and internal secret are <strong>not</strong> here — they live in
        Supabase function secrets (see the checklist).
      </p>

      {msg && <div className={`admin-et-msg ${msg.type}`}>{msg.text}</div>}

      {loading ? (
        <div className="admin-empty">Loading…</div>
      ) : (
        <>
        {/* Sender 1 — notification / transactional emails */}
        <div className="admin-card">
          <div className="admin-et-cardhead">Notification emails <span className="note">— ticket replies, alerts &amp; other transactional messages</span></div>
          <div className="admin-et-form">
            <div className="admin-et-field admin-et-grow">
              <label>From address</label>
              <input type="text" value={cfg.from_email} onChange={(e) => set("from_email", e.target.value)} placeholder="noreply@brandcommand.app" spellCheck={false} />
            </div>
            <div className="admin-et-field admin-et-grow">
              <label>From name</label>
              <input type="text" value={cfg.from_name} onChange={(e) => set("from_name", e.target.value)} placeholder="Brand Command" maxLength={120} />
            </div>
            <div className="admin-et-field admin-et-grow">
              <label><span>Reply-to address </span><span className="note">(optional)</span></label>
              <input type="text" value={cfg.reply_to} onChange={(e) => set("reply_to", e.target.value)} placeholder="info@brandcommand.app" spellCheck={false} />
            </div>

            <div className="admin-et-foot">
              <button className="btn"
                onClick={() => save({ from_email: cfg.from_email, from_name: cfg.from_name, reply_to: cfg.reply_to }, "Notification sender saved.")}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save notification sender"}
              </button>
            </div>

            <label className="admin-et-toggle">
              <input type="checkbox" checked={cfg.enabled} onChange={(e) => toggleEnabled(e.target.checked)} />
              <span>
                <strong>Enable sending</strong>
                <span className="note"> — when off, only test emails go out. Turn on once your domain is verified and a test arrives. (Applies to notification emails; the nurture sequence sends independently.)</span>
              </span>
            </label>

            <div className="admin-et-test">
              <div className="admin-et-field">
                <label>Send a test email to</label>
                <input type="text" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" spellCheck={false} />
              </div>
              <button className="secondary" onClick={handleTest} disabled={testing}>
                {testing ? "Sending…" : "Send test"}
              </button>
            </div>
          </div>
        </div>

        {/* Sender 2 — nurture sequence (build-the-brand guide emails) */}
        <div className="admin-card">
          <div className="admin-et-cardhead">Nurture sequence <span className="note">— the build-the-brand guide emails (E1–E6)</span></div>
          <div className="admin-et-form">
            <div className="admin-et-field admin-et-grow">
              <label>From address</label>
              <input type="text" value={cfg.nurture_from_email} onChange={(e) => set("nurture_from_email", e.target.value)} placeholder="info@brandcommand.app" spellCheck={false} />
            </div>
            <div className="admin-et-field admin-et-grow">
              <label>From name</label>
              <input type="text" value={cfg.nurture_from_name} onChange={(e) => set("nurture_from_name", e.target.value)} placeholder="Brand Command" maxLength={120} />
            </div>
            <div className="admin-et-field admin-et-grow">
              <label><span>Reply-to address </span><span className="note">(optional)</span></label>
              <input type="text" value={cfg.nurture_reply_to} onChange={(e) => set("nurture_reply_to", e.target.value)} placeholder="info@brandcommand.app" spellCheck={false} />
            </div>

            <div className="admin-et-foot">
              <button className="btn"
                onClick={() => save({ nurture_from_email: cfg.nurture_from_email, nurture_from_name: cfg.nurture_from_name, nurture_reply_to: cfg.nurture_reply_to }, "Nurture sender saved.")}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save nurture sender"}
              </button>
            </div>
          </div>
        </div>
        </>
      )}

      <details className="admin-et-checklist">
        <summary>Brevo setup checklist</summary>
        <ol>
          <li>Create a free <strong>Brevo</strong> account (300 emails/day free tier).</li>
          <li>Add and verify the domain <code>brandcommand.app</code> in Brevo → paste the
            SPF / DKIM / DMARC DNS records it gives you into your domain's DNS. This is what
            keeps emails out of spam.</li>
          <li>In Supabase → Edge Functions → <strong>Secrets</strong>, set:
            <ul>
              <li><code>BREVO_API_KEY</code> — the Brevo transactional API key</li>
              <li><code>EMAIL_INTERNAL_SECRET</code> — any long random string (lets server jobs call the sender)</li>
            </ul>
            These never appear in this UI or the codebase.</li>
          <li>Set the <strong>From address</strong> above to a verified Brevo sender on the domain.</li>
          <li>Send yourself a <strong>test</strong> (works even while sending is off) and confirm it arrives.</li>
          <li>Flip <strong>Enable sending</strong> on. Real notifications now go out.</li>
        </ol>
      </details>
    </section>
  );
}

function EtPanel({
  title, bgColor, logoUrl, logoPlacement, text, textPlaceholder,
  onBgColor, onLogoUrl, onLogoPlacement, onText,
}) {
  return (
    <div className="admin-et-panel">
      <div className="admin-et-panel-head">{title}</div>
      <div className="admin-et-body">
        <div className="admin-et-row">
          <div className="admin-et-field">
            <label>Background color</label>
            <div className="admin-et-color">
              <input type="color" value={bgColor} onChange={(e) => onBgColor(e.target.value)} aria-label={`${title} background color`} />
              <input type="text" value={bgColor} onChange={(e) => onBgColor(e.target.value)} maxLength={7} spellCheck={false} />
            </div>
          </div>

          {logoUrl ? (
            <div className="admin-et-field">
              <label>Image placement</label>
              <div className="admin-et-seg">
                {PLACEMENTS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    className={logoPlacement === p ? "active" : ""}
                    onClick={() => onLogoPlacement(p)}
                  >
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <div className="admin-et-field admin-et-grow">
          <label><span>Logo / image URL </span><span className="note">(optional)</span></label>
          <input
            type="text"
            value={logoUrl}
            onChange={(e) => onLogoUrl(e.target.value)}
            placeholder="https://app.brandcommand.app/logo.png"
            spellCheck={false}
          />
        </div>

        <div className="admin-et-field admin-et-grow">
          <label><span>Text </span><span className="note">(optional)</span></label>
          <textarea
            value={text}
            onChange={(e) => onText(e.target.value)}
            rows={2}
            placeholder={textPlaceholder}
          />
        </div>
      </div>
    </div>
  );
}

function EtPreview({ form }) {
  const align = (p) => (p === "center" ? "center" : p === "right" ? "flex-end" : "flex-start");
  const textAlign = (p, hasLogo) => (hasLogo ? p : "left");

  return (
    <div className="admin-et-preview">
      <div className="admin-et-preview-head">
        <span>Preview</span>
        <span className="approx">Approximate rendering</span>
      </div>
      <div className="admin-et-stage">
        <div className="admin-et-mail">
          {/* Header */}
          <div className="admin-et-mail-hdr" style={{ background: form.header_bg_color }}>
            {form.header_logo_url && (
              <div className="admin-et-logo-line" style={{ justifyContent: align(form.header_logo_placement), marginBottom: form.header_text ? 8 : 0 }}>
                <img src={form.header_logo_url} alt="" />
              </div>
            )}
            {form.header_text ? (
              <div style={{ color: "#fff", fontSize: 14, fontWeight: 600, whiteSpace: "pre-wrap", textAlign: textAlign(form.header_logo_placement, !!form.header_logo_url) }}>
                {form.header_text}
              </div>
            ) : !form.header_logo_url ? (
              <div style={{ color: "rgba(255,255,255,.4)", fontSize: 12, fontStyle: "italic" }}>Header — no content set</div>
            ) : null}
          </div>

          {/* Body placeholder */}
          <div className="admin-et-mail-body">
            {[70, 92, 55].map((w, i) => (
              <div key={i} className="ln" style={{ width: `${w}%` }} />
            ))}
          </div>

          {/* Footer */}
          <div className="admin-et-mail-ftr" style={{ background: form.footer_bg_color }}>
            {form.footer_logo_url && (
              <div className="admin-et-logo-line" style={{ justifyContent: align(form.footer_logo_placement), marginBottom: form.footer_text ? 6 : 0 }}>
                <img src={form.footer_logo_url} alt="" />
              </div>
            )}
            {form.footer_text ? (
              <div style={{ color: "#9ca3af", fontSize: 11, whiteSpace: "pre-wrap", textAlign: textAlign(form.footer_logo_placement, !!form.footer_logo_url) }}>
                {form.footer_text}
              </div>
            ) : !form.footer_logo_url ? (
              <div style={{ color: "#d1d5db", fontSize: 11, fontStyle: "italic" }}>Footer — no content set</div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
